import { useMemo, useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Activity, Wifi, WifiOff, Cpu, Thermometer, Droplets, Wind, Signal,
  RefreshCw, Users, HardDrive, Clock, Server, AlertTriangle,
  CheckCircle, Bell, Radio, Database, Cloud, Globe, RotateCcw, AlertOctagon,
  ChevronDown, ChevronRight
} from 'lucide-react'
import { useDeviceStatus } from '../context/DeviceStatusContext'
import { useAdminData } from '../context/AdminDataContext'
import { adminApi } from '../services/ApiService'
import './Overview.css'

// ── Helpers ──────────────────────────────────────────────

function avg(arr) {
  if (!arr.length) return 0
  return arr.reduce((a, b) => a + b, 0) / arr.length
}

function formatUptime(seconds) {
  if (!seconds || seconds <= 0) return '-'
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}
function formatInterval(ms) {
  if (!ms || ms <= 0) return '—'
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  const rs = s % 60
  return rs > 0 ? `${m}m ${rs}s` : `${m}m`
}
function rssiQuality(rssi) {
  if (rssi >= -50) return { label: 'Excelente', pct: 100, color: '#10b981' }
  if (rssi >= -60) return { label: 'Buena', pct: 80, color: '#22c55e' }
  if (rssi >= -70) return { label: 'Aceptable', pct: 60, color: '#f59e0b' }
  if (rssi >= -80) return { label: 'Débil', pct: 40, color: '#f59e0b' }
  return { label: 'Mala', pct: 20, color: '#ef4444' }
}

const RESET_REASONS = {
  1: 'Power On', 2: 'External Reset', 3: 'Software Reset',
  4: 'Panic / Exception', 5: 'Interrupt WDT', 6: 'Task WDT',
  7: 'Other WDT', 8: 'Deep Sleep', 9: 'Brownout', 10: 'SDIO Reset',
}

function getResetReasonLabel(code) {
  return RESET_REASONS[code] || `Desconocido (${code})`
}

// Agrupa una lista de eventos por mac, ordenando grupos por evento más reciente
function groupByMac(events) {
  const map = new Map()
  for (const ev of events) {
    const mac = (ev.mac || '').toUpperCase()
    if (!map.has(mac)) map.set(mac, { mac, name: ev.name || null, events: [] })
    const g = map.get(mac)
    if (ev.name && !g.name) g.name = ev.name
    g.events.push(ev)
  }
  // cada grupo ya viene ordenado por timestamp desc desde el backend
  return [...map.values()].sort((a, b) => (b.events[0]?.timestamp ?? 0) - (a.events[0]?.timestamp ?? 0))
}

function getResetReasonColor(code) {
  if ([4, 5, 6, 7].includes(code)) return '#ef4444'
  if (code === 9) return '#f59e0b'
  if ([1, 2, 8].includes(code)) return '#10b981'
  return '#94a3b8'
}

// ── Ring Chart ───────────────────────────────────────────

function RingChart({ value, total, label, color = '#10b981' }) {
  const pct = total > 0 ? (value / total) * 100 : 0
  const radius = 44
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (pct / 100) * circumference

  return (
    <div className="ov-ring">
      <svg width="100" height="100" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={radius} fill="none" stroke="rgba(51,65,85,0.5)" strokeWidth="8" />
        <circle cx="50" cy="50" r={radius} fill="none"
          stroke={color} strokeWidth="8" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.6s ease', transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }}
        />
      </svg>
      <div className="ov-ring-center">
        <span className="ov-ring-value">{value}</span>
        <span className="ov-ring-label">{label}</span>
      </div>
    </div>
  )
}

// ── MiniGauge ────────────────────────────────────────────

function MiniGauge({ value, max = 100, color = '#10b981' }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0
  return (
    <span className="ov-mini-gauge">
      <span className="ov-mini-gauge-track">
        <span className="ov-mini-gauge-fill" style={{ width: `${pct}%`, background: color }} />
      </span>
    </span>
  )
}

// ── Alert Badge ──────────────────────────────────────────

function AlertBadge({ alert }) {
  const icons = {
    wifi_weak: <Signal size={14} />,
    low_memory: <HardDrive size={14} />,
    heap_fragmentation: <HardDrive size={14} />,
    disconnection: <WifiOff size={14} />,
    offline: <WifiOff size={14} />,
    cloudwatch_alarm: <AlertTriangle size={14} />,
    sensor_failure: <Thermometer size={14} />,
  }
  const label = alert.name || alert.mac
  return (
    <div className={`ov-alert-item ${alert.severity}`}>
      <span className="ov-alert-icon">{icons[alert.type] || <AlertTriangle size={14} />}</span>
      <div className="ov-alert-body">
        {alert.name && alert.name !== alert.mac ? (
          <>
            <span className="ov-conn-log-name">{alert.name}</span>
            <span className="ov-alert-mac">{alert.mac}</span>
          </>
        ) : (
          <span className="ov-alert-mac">{alert.mac}</span>
        )}
        <span className="ov-alert-msg">{alert.message}</span>
      </div>
      {alert.timestamp && (
        <span className="ov-alert-time">
          {new Date(alert.timestamp).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
        </span>
      )}
    </div>
  )
}

// ── Main Component ───────────────────────────────────────

export default function Overview() {
  const navigate = useNavigate()
  const { wsDevices: activeDevices, wsConnected, wsStats, connectionLog, sensorAlerts, telemetryIntervals } = useDeviceStatus()
  const { devices: allDevices, health, subscriptions: subStats, alerts, loading, lastUpdate, refresh } = useAdminData()
  const [lifecycleHistory, setLifecycleHistory] = useState([])
  const [restartLog, setRestartLog] = useState([])
  const [deviceErrors, setDeviceErrors] = useState([])
  const [connExpanded, setConnExpanded] = useState(new Set())
  const [restartExpanded, setRestartExpanded] = useState(new Set())
  const [errorsExpanded, setErrorsExpanded] = useState(new Set())

  const toggleExpand = (setter, mac) =>
    setter(prev => { const s = new Set(prev); s.has(mac) ? s.delete(mac) : s.add(mac); return s })

  // Fuente única: el backend hace el pareo/filtrado de reconexiones y el cálculo
  // de reinicios. El frontend solo consume los dos endpoints.
  //
  // Traemos hasta 7 días de historial (TTL natural de la tabla).
  // El acordeón agrupa por MAC así el log no se vuelve interminable visualmente.
  const CONN_PARAMS = { hours: 168, limit: 500 }
  const RESTART_PARAMS = { hours: 168, limit: 500 }
  const ERRORS_PARAMS = { limit: 200, days: 7 }

  const fetchLogs = useCallback(() => {
    return Promise.all([
      adminApi.get('/admin/logs/lifecycle', CONN_PARAMS),
      adminApi.get('/admin/logs/restarts', RESTART_PARAMS),
      adminApi.get('/admin/monitoring/device-errors', ERRORS_PARAMS),
    ]).then(([lc, rs, errs]) => {
      if (lc?.events) setLifecycleHistory(lc.events)
      if (rs?.restarts) setRestartLog(rs.restarts)
      if (errs?.errors) setDeviceErrors(errs.errors)
    }).catch(err => console.warn('Could not fetch lifecycle/restarts:', err.message))
  }, [])

  useEffect(() => { fetchLogs() }, [fetchLogs])

  // WS-triggered debounced refetch — agrupa ráfagas dentro de 3s
  const lastWsTimestamp = connectionLog[0]?.timestamp ?? 0
  useEffect(() => {
    if (!lastWsTimestamp) return
    const id = setTimeout(fetchLogs, 3000)
    return () => clearTimeout(id)
  }, [lastWsTimestamp, fetchLogs])

  // Enriquecer Log de Conexiones con el nombre del dispositivo desde allDevices.
  const mergedConnectionLog = useMemo(() => {
    const nameByMac = new Map()
    allDevices?.forEach(d => {
      if (d.mac && d.name) nameByMac.set(d.mac.toUpperCase(), d.name)
    })
    return lifecycleHistory.map(entry => ({
      ...entry,
      name: entry.name || nameByMac.get(entry.mac?.toUpperCase()) || null,
    }))
  }, [lifecycleHistory, allDevices])

  // Restart log: viene listo desde el backend. Solo renombramos `resetReason` →
  // `resetReasonCode` para mantener compatibilidad con el render existente.
  const enrichedRestartLog = useMemo(() => {
    const nameByMac = new Map()
    allDevices?.forEach(d => {
      if (d.mac && d.name) nameByMac.set(d.mac.toUpperCase(), d.name)
    })
    return restartLog.map(r => ({
      ...r,
      resetReasonCode: r.resetReason ?? null,
      name: r.name || nameByMac.get(r.mac?.toUpperCase()) || null,
    }))
  }, [restartLog, allDevices])

  // ── Computed ────────────────────────────────────────────

  // Only include WS devices confirmed online (exclude lifecycle disconnects)
  const activeMACsSet = useMemo(
    () => new Set(
      (activeDevices || [])
        .filter(d => d.status !== 'offline' && d.online !== false)
        .map(d => d.mac?.toUpperCase())
    ),
    [activeDevices]
  )

  // Track MACs that WS explicitly says are offline (overrides stale REST isOnline)
  const offlineMACsSet = useMemo(
    () => new Set(
      (activeDevices || [])
        .filter(d => d.status === 'offline' || d.online === false)
        .map(d => d.mac?.toUpperCase())
    ),
    [activeDevices]
  )

  const deviceAnalytics = useMemo(() => {
    const isDeviceOnline = (d) => {
      const mac = (d.MAC || d.mac || '').toUpperCase()
      if (offlineMACsSet.has(mac)) return false
      return d.isOnline || activeMACsSet.has(mac)
    }
    const online = allDevices.filter(isDeviceOnline).length
    const offline = allDevices.length - online
    const onlinePct = allDevices.length > 0 ? Math.round((online / allDevices.length) * 100) : 0

    const models = {}
    allDevices.forEach(d => { const m = d.model || 'Desconocido'; models[m] = (models[m] || 0) + 1 })
    const uniqueUsers = new Set(allDevices.map(d => d.authUser?.id || d.accountId).filter(Boolean)).size

    // Breakdown by model: { 'GP-BOX': { online: 5, offline: 3 }, ... }
    const modelBreakdown = {}
    allDevices.forEach(d => {
      const m = d.model || 'Desconocido'
      if (!modelBreakdown[m]) modelBreakdown[m] = { online: 0, offline: 0 }
      if (isDeviceOnline(d)) modelBreakdown[m].online++
      else modelBreakdown[m].offline++
    })

    return { online, offline, onlinePct, models, modelBreakdown, uniqueUsers }
  }, [allDevices, activeMACsSet, offlineMACsSet])

  const mergedOnlineDevices = useMemo(() => {
    const map = new Map()

    allDevices.forEach(d => {
      if (!d.isOnline) return
      const mac = (d.MAC || d.mac || '').toUpperCase()
      if (!mac) return

      map.set(mac, {
        mac: d.MAC || d.mac, model: d.model || d.liveData?.model || null, name: d.name,
        temperature: d.liveData?.temperature ?? null,
        humidity: d.liveData?.humidity ?? null,
        dpv: d.liveData?.dpv ?? null,
        rssi: d.liveData?.heartbeat?.rssi ?? null,
        uptime: d.liveData?.heartbeat?.uptime ?? null,
        heap: d.liveData?.heartbeat?.heap ?? null,
        firmwareVersion: d.firmwareVersion || d.liveData?.firmwareVersion || null,
        heartbeat: d.liveData?.heartbeat ?? null,
        sensorName: d.liveData?.sensorName ?? null,
      })
    })

      ; (activeDevices || []).forEach(d => {
        const mac = (d.mac || '').toUpperCase()
        if (!mac) return
        const existing = map.get(mac) || {}
        map.set(mac, {
          ...existing, ...d,
          temperature: d.temperature ?? existing.temperature,
          humidity: d.humidity ?? existing.humidity,
          rssi: d.heartbeat?.rssi ?? d.rssi ?? existing.rssi,
          uptime: d.heartbeat?.uptime ?? d.uptime ?? existing.uptime,
          heap: d.heartbeat?.heap ?? d.heap ?? existing.heap,
          // sensorName es propiedad del hardware, no de la telemetría en vivo.
          // Preservar el valor del initial load si el WebSocket no lo trae.
          sensorName: d.sensorName ?? existing.sensorName ?? null,
        })
      })
    return [...map.values()]
  }, [allDevices, activeDevices])

  const sensorStats = useMemo(() => {
    if (!mergedOnlineDevices.length) return null
    const temps = mergedOnlineDevices.map(d => d.temperature).filter(v => v != null)
    const humids = mergedOnlineDevices.map(d => d.humidity).filter(v => v != null)
    const dpvs = mergedOnlineDevices.map(d => d.dpv).filter(v => v != null)
    return {
      temperature: temps.length ? { avg: avg(temps), min: Math.min(...temps), max: Math.max(...temps), count: temps.length } : null,
      humidity: humids.length ? { avg: avg(humids), min: Math.min(...humids), max: Math.max(...humids), count: humids.length } : null,
      dpv: dpvs.length ? { avg: avg(dpvs), min: Math.min(...dpvs), max: Math.max(...dpvs) } : null,
    }
  }, [mergedOnlineDevices])

  const healthStats = useMemo(() => {
    if (!mergedOnlineDevices.length) return null
    const rssis = mergedOnlineDevices.map(d => d.rssi).filter(v => v != null)
    const uptimes = mergedOnlineDevices.map(d => d.uptime).filter(v => v != null)
    if (!rssis.length && !uptimes.length) return null
    const rssiDist = { excellent: 0, good: 0, fair: 0, weak: 0 }
    rssis.forEach(r => { if (r >= -50) rssiDist.excellent++; else if (r >= -60) rssiDist.good++; else if (r >= -70) rssiDist.fair++; else rssiDist.weak++ })
    return {
      rssi: { avg: rssis.length ? avg(rssis) : 0, dist: rssiDist, total: rssis.length },
      uptime: { avg: uptimes.length ? avg(uptimes) : 0, max: uptimes.length ? Math.max(...uptimes) : 0 }
    }
  }, [mergedOnlineDevices])

  const serverUp = health?.status === 'healthy'

  // Merge backend alerts with real-time sensor alerts
  const sensorAlertEntries = useMemo(() => {
    if (!sensorAlerts || sensorAlerts.size === 0) return []
    return Array.from(sensorAlerts.entries()).map(([mac, info]) => {
      const dev = allDevices.find(d => (d.MAC || d.mac || '').toUpperCase() === mac)
      return {
        type: 'sensor_failure',
        severity: 'warning',
        mac,
        name: dev?.name || mac,
        message: info.message,
        timestamp: info.timestamp,
      }
    })
  }, [sensorAlerts, allDevices])

  const allAlerts = useMemo(() => [...sensorAlertEntries, ...alerts], [sensorAlertEntries, alerts])
  const criticalAlerts = allAlerts.filter(a => a.severity === 'critical')
  const warningAlerts = allAlerts.filter(a => a.severity === 'warning')

  // ── Render ──────────────────────────────────────────────

  return (
    <div className="page">
      {/* Header */}
      <div className="ov-header">
        <div>
          <h1 className="ov-title">Overview</h1>
          <p className="ov-subtitle">Panel de control unificado — GrowPro IoT</p>
        </div>
        <div className="ov-header-actions">
          {lastUpdate && (
            <span className="ov-last-update">{lastUpdate.toLocaleTimeString('es-ES')}</span>
          )}
          <button className="ov-refresh-btn" onClick={refresh} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
          </button>
        </div>
      </div>

      {/* ── Row 1: KPI cards ─────────────────────────────── */}
      <div className="ov-kpi-grid">
        <div className="ov-kpi">
          <div className="ov-kpi-icon green"><Cpu size={22} /></div>
          <div className="ov-kpi-data">
            <div className="ov-kpi-value">{allDevices.length}</div>
            <div className="ov-kpi-label">Dispositivos</div>
          </div>
        </div>
        <div className="ov-kpi">
          <div className="ov-kpi-icon blue"><Wifi size={22} /></div>
          <div className="ov-kpi-data">
            <div className="ov-kpi-value">
              {deviceAnalytics.online}<span className="ov-kpi-secondary">/{allDevices.length}</span>
            </div>
            <div className="ov-kpi-label">En línea ({deviceAnalytics.onlinePct}%)</div>
          </div>
        </div>
        <div className="ov-kpi">
          <div className="ov-kpi-icon purple"><Users size={22} /></div>
          <div className="ov-kpi-data">
            <div className="ov-kpi-value">{deviceAnalytics.uniqueUsers}</div>
            <div className="ov-kpi-label">Clientes</div>
          </div>
        </div>
        <div className="ov-kpi">
          <div className="ov-kpi-icon cyan"><Activity size={22} /></div>
          <div className="ov-kpi-data">
            <div className="ov-kpi-value">{subStats?.active || 0}</div>
            <div className="ov-kpi-label">Suscripciones activas</div>
          </div>
        </div>
        <div className={`ov-kpi ${criticalAlerts.length > 0 ? 'has-alert' : ''}`}>
          <div className={`ov-kpi-icon ${criticalAlerts.length > 0 ? 'red' : 'green'}`}>
            {criticalAlerts.length > 0 ? <AlertTriangle size={22} /> : <CheckCircle size={22} />}
          </div>
          <div className="ov-kpi-data">
            <div className="ov-kpi-value">{allAlerts.length}</div>
            <div className="ov-kpi-label">
              {criticalAlerts.length > 0 ? `${criticalAlerts.length} críticas` : 'Sin alertas críticas'}
            </div>
          </div>
        </div>
      </div>

      {/* ── Row 2: Infrastructure + Alerts ─────────────── */}
      <div className="ov-grid-2">
        {/* Infrastructure Health */}
        <div className="ov-card">
          <h3 className="ov-card-title"><Server size={16} /> Infraestructura</h3>
          <div className={`ov-health-banner ${serverUp ? 'ok' : 'error'}`}>
            <span>{serverUp ? '🟢' : '🔴'}</span>
            <div>
              <div className="ov-health-status">{serverUp ? 'Operativa' : 'Con problemas'}</div>
              <div className="ov-health-detail">AWS Lambda + API Gateway + IoT Core</div>
            </div>
          </div>

          <div className="ov-services">
            {[
              { label: 'DynamoDB', key: 'dynamodb', icon: <Database size={13} />, detail: `${deviceAnalytics.online} conectados` },
              { label: 'IoT Core', key: 'iotEndpoint', icon: <Cloud size={13} />, detail: `${wsStats?.totalDevices ?? '—'} devices` },
              { label: 'Cognito', key: 'cognito', icon: <Globe size={13} />, detail: `${subStats?.total || '—'} perfiles` },
              { label: 'WebSocket', key: 'ws', status: wsConnected ? 'ok' : 'offline', icon: <Activity size={13} />, detail: `${wsStats?.uniqueClients ?? 0} viendo` },
            ].map(({ label, key, status: overrideStatus, icon, detail }) => {
              const s = overrideStatus || health?.services?.[key] || 'unknown'
              const isUp = s === 'ok' || s === 'configured'
              return (
                <div key={key} className="ov-service-row">
                  <span className="ov-service-name">{icon} {label}</span>
                  <div className="ov-service-right">
                    {detail && <span className="ov-service-detail">{detail}</span>}
                    <span className={`ov-service-status ${isUp ? 'up' : 'down'}`}>
                      {isUp ? '● Online' : '○ Offline'}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Alerts Panel */}
        <div className="ov-card">
          <h3 className="ov-card-title">
            <Bell size={16} /> Alertas
            {allAlerts.length > 0 && <span className="ov-badge">{allAlerts.length}</span>}
          </h3>
          {allAlerts.length === 0 ? (
            <div className="ov-empty-alerts">
              <CheckCircle size={32} style={{ opacity: 0.3 }} />
              <p>Todo en orden — sin alertas activas</p>
            </div>
          ) : (
            <div className="ov-alerts-list">
              {allAlerts.slice(0, 8).map((alert, i) => {
                const enrichedAlert = { ...alert }
                if (!enrichedAlert.name || enrichedAlert.name === enrichedAlert.mac) {
                  const dev = allDevices.find(d => (d.MAC || d.mac || '').toUpperCase() === (enrichedAlert.mac || '').toUpperCase())
                  if (dev?.name) enrichedAlert.name = dev.name
                }
                return <AlertBadge key={i} alert={enrichedAlert} />
              })}
              {allAlerts.length > 8 && (
                <div className="ov-alerts-more">+{allAlerts.length - 8} alertas más</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Row 2.5: Connection Log + Restart Log (two columns) ── */}
      <div className="ov-grid-2">
        <div className="ov-card ov-conn-log-card">
          <h3 className="ov-card-title">
            <Radio size={16} /> Log de Conexiones
            {mergedConnectionLog.length > 0 && <span className="ov-badge ov-badge-info">{mergedConnectionLog.length}</span>}
            <span className="ov-card-subtitle">Últimos 7 días + tiempo real</span>
          </h3>
          {mergedConnectionLog.length === 0 ? (
            <div className="ov-empty-alerts">
              <Radio size={32} style={{ opacity: 0.3 }} />
              <p>Sin eventos de conexión recientes</p>
            </div>
          ) : (
            <div className="ov-conn-log-list">
              {groupByMac(mergedConnectionLog).map(group => {
                const expanded = connExpanded.has(group.mac)
                const hasMany = group.events.length > 1
                const latest = group.events[0]
                const hasDisc = group.events.some(e => e.event === 'disconnected')
                const headerClass = hasDisc ? 'critical' : 'info'
                return (
                  <div key={group.mac} className="ov-group-block">
                    <div
                      className={`ov-alert-item ov-group-header ${headerClass}`}
                      onClick={() => hasMany && toggleExpand(setConnExpanded, group.mac)}
                      style={{ cursor: hasMany ? 'pointer' : 'default' }}
                    >
                      <span className="ov-alert-icon">
                        {hasDisc ? <WifiOff size={14} /> : <Wifi size={14} />}
                      </span>
                      <div className="ov-alert-body">
                        <span className="ov-conn-log-name ov-conn-log-name--link" onClick={e => { e.stopPropagation(); navigate(`/devices/${encodeURIComponent(group.mac)}`) }}>{group.name || group.mac}</span>
                        {group.name && <span className="ov-alert-mac">{group.mac}</span>}
                        {hasMany
                          ? <span className="ov-alert-msg ov-group-summary">{group.events.length} eventos</span>
                          : <span className="ov-alert-msg">
                            {latest.event === 'connected' ? 'Se conectó' : 'Se desconectó'}
                            {latest.event === 'disconnected' && latest.disconnectReason && (
                              <span className="ov-disconnect-reason"> — {latest.disconnectReason.replace(/_/g, ' ').toLowerCase()}</span>
                            )}
                          </span>
                        }
                      </div>
                      <span className="ov-alert-time">
                        {new Date(latest.timestamp).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </span>
                      {hasMany && <span className="ov-chevron">{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>}
                    </div>
                    {hasMany && expanded && (
                      <div className="ov-group-children">
                        {group.events.map((entry, i) => (
                          <div key={`${entry.mac}-${entry.timestamp}-${i}`} className={`ov-alert-item ov-group-child ${entry.event === 'connected' ? 'info' : 'critical'}`}>
                            <span className="ov-alert-icon">
                              {entry.event === 'connected' ? <Wifi size={12} /> : <WifiOff size={12} />}
                            </span>
                            <div className="ov-alert-body">
                              <span className="ov-alert-msg">
                                {entry.event === 'connected' ? 'Se conectó' : 'Se desconectó'}
                                {entry.event === 'disconnected' && entry.disconnectReason && (
                                  <span className="ov-disconnect-reason"> — {entry.disconnectReason.replace(/_/g, ' ').toLowerCase()}</span>
                                )}
                              </span>
                            </div>
                            <span className="ov-alert-time">
                              {new Date(entry.timestamp).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="ov-card ov-conn-log-card">
          <h3 className="ov-card-title">
            <RotateCcw size={16} /> Log de Reinicios
            {enrichedRestartLog.length > 0 && <span className="ov-badge ov-badge-info">{enrichedRestartLog.length}</span>}
            <span className="ov-card-subtitle">Últimos 7 días + tiempo real</span>
          </h3>
          {enrichedRestartLog.length === 0 ? (
            <div className="ov-empty-alerts">
              <RotateCcw size={32} style={{ opacity: 0.3 }} />
              <p>Sin reinicios recientes</p>
            </div>
          ) : (
            <div className="ov-conn-log-list">
              {groupByMac(enrichedRestartLog).map(group => {
                const expanded = restartExpanded.has(group.mac)
                const hasMany = group.events.length > 1
                const latest = group.events[0]
                const hasSevere = group.events.some(e => e.panicCount > 0 || e.wdtCount >= 3)
                const headerClass = hasSevere ? 'error' : 'warning'
                return (
                  <div key={group.mac} className="ov-group-block">
                    <div
                      className={`ov-alert-item ov-group-header ${headerClass}`}
                      onClick={() => hasMany && toggleExpand(setRestartExpanded, group.mac)}
                      style={{ cursor: hasMany ? 'pointer' : 'default' }}
                    >
                      <span className="ov-alert-icon"><RotateCcw size={14} /></span>
                      <div className="ov-alert-body">
                        <span className="ov-conn-log-name ov-conn-log-name--link" onClick={e => { e.stopPropagation(); navigate(`/devices/${encodeURIComponent(group.mac)}`) }}>{group.name || group.mac}</span>
                        {group.name && <span className="ov-alert-mac">{group.mac}</span>}
                        {hasMany
                          ? <span className="ov-alert-msg ov-group-summary">{group.events.length} reinicios</span>
                          : <span className="ov-alert-msg">
                            {latest.resetReasonCode != null ? 'Se reinició' : 'Se conectó'}
                            {latest.resetReasonCode != null && (
                              <span className="ov-reset-reason" style={{ color: getResetReasonColor(latest.resetReasonCode) }}>
                                {' '}— {getResetReasonLabel(latest.resetReasonCode)}
                              </span>
                            )}
                            {latest.panicCount > 0 && <span className="ov-reset-reason" style={{ color: '#ef4444' }}> · {latest.panicCount} panic{latest.panicCount > 1 ? 's' : ''}</span>}
                            {latest.wdtCount >= 3 && <span className="ov-reset-reason" style={{ color: '#f59e0b' }}> · WDT ×{latest.wdtCount}</span>}
                          </span>
                        }
                      </div>
                      <span className="ov-alert-time">
                        {new Date(latest.timestamp).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </span>
                      {hasMany && <span className="ov-chevron">{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>}
                    </div>
                    {hasMany && expanded && (
                      <div className="ov-group-children">
                        {group.events.map((entry, i) => {
                          const hasRR = entry.resetReasonCode != null
                          const isSev = entry.panicCount > 0 || entry.wdtCount >= 3
                          return (
                            <div key={`${entry.mac}-${entry.timestamp}-${i}`} className={`ov-alert-item ov-group-child ${isSev ? 'error' : 'warning'}`}>
                              <span className="ov-alert-icon"><RotateCcw size={12} /></span>
                              <div className="ov-alert-body">
                                <span className="ov-alert-msg">
                                  {hasRR ? 'Se reinició' : 'Se conectó'}
                                  {hasRR && <span className="ov-reset-reason" style={{ color: getResetReasonColor(entry.resetReasonCode) }}> — {getResetReasonLabel(entry.resetReasonCode)}</span>}
                                  {entry.panicCount > 0 && <span className="ov-reset-reason" style={{ color: '#ef4444' }}> · {entry.panicCount} panic{entry.panicCount > 1 ? 's' : ''}</span>}
                                  {entry.wdtCount >= 3 && <span className="ov-reset-reason" style={{ color: '#f59e0b' }}> · WDT ×{entry.wdtCount}</span>}
                                </span>
                              </div>
                              <span className="ov-alert-time">
                                {new Date(entry.timestamp).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Row 2.6: Errores Recientes ─────────────────── */}
      <div className="ov-card ov-conn-log-card">
        <h3 className="ov-card-title">
          <AlertOctagon size={16} /> Errores Recientes
          {deviceErrors.length > 0 && <span className="ov-badge ov-badge-error">{deviceErrors.length}</span>}
          <span className="ov-card-subtitle">Últimos 7 días</span>
        </h3>
        <div className="ov-conn-log-list">
          {deviceErrors.length === 0 ? (
            <div className="ov-empty-state">Sin errores registrados</div>
          ) : (() => {
            // Enriquecer con nombre del dispositivo
            const enrichedErrors = deviceErrors.map(err => {
              const dev = allDevices.find(d => (d.MAC || d.mac || '').toUpperCase() === (err.mac || '').toUpperCase())
              return { ...err, name: dev?.name || null }
            })
            return groupByMac(enrichedErrors).map(group => {
              const expanded = errorsExpanded.has(group.mac)
              const hasMany = group.events.length > 1
              const latest = group.events[0]
              const worstSev = group.events.some(e => e.severity === 'critical') ? 'critical'
                : group.events.some(e => e.severity === 'error') ? 'error'
                  : group.events.some(e => e.severity === 'warn') ? 'warning' : 'info'
              const sevColor = worstSev === 'critical' || worstSev === 'error' ? '#ef4444' : worstSev === 'warning' ? '#f59e0b' : '#94a3b8'
              const renderErrMsg = (err) => {
                const sc = err.severity === 'critical' ? '#ef4444' : err.severity === 'error' ? '#f97316' : err.severity === 'warn' ? '#f59e0b' : '#94a3b8'
                return (
                  <>
                    <span style={{ color: sc, fontWeight: 600, marginRight: '0.25rem' }}>[{err.severity?.toUpperCase()}]</span>
                    <span style={{ color: '#94a3b8', marginRight: '0.25rem' }}>{err.code}</span>
                    {err.component && <span style={{ color: '#64748b', marginRight: '0.25rem' }}>({err.component})</span>}
                    {err.message}
                    {err.count > 1 && <span style={{ color: '#64748b', marginLeft: '0.25rem' }}>×{err.count}</span>}
                  </>
                )
              }
              return (
                <div key={group.mac} className="ov-group-block">
                  <div
                    className={`ov-alert-item ov-group-header ${worstSev}`}
                    onClick={() => hasMany && toggleExpand(setErrorsExpanded, group.mac)}
                    style={{ cursor: hasMany ? 'pointer' : 'default' }}
                  >
                    <span className="ov-alert-icon" style={{ color: sevColor }}><AlertOctagon size={14} /></span>
                    <div className="ov-alert-body">
                      <span className="ov-conn-log-name ov-conn-log-name--link" onClick={e => { e.stopPropagation(); navigate(`/devices/${encodeURIComponent(group.mac)}`) }}>{group.name || group.mac}</span>
                      {group.name && <span className="ov-alert-mac">{group.mac}</span>}
                      {hasMany
                        ? <span className="ov-alert-msg ov-group-summary">{group.events.length} errores</span>
                        : <span className="ov-alert-msg">{renderErrMsg(latest)}</span>
                      }
                    </div>
                    <span className="ov-alert-time">
                      {new Date(latest.timestamp).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {hasMany && <span className="ov-chevron">{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>}
                  </div>
                  {hasMany && expanded && (
                    <div className="ov-group-children">
                      {group.events.map((err, i) => {
                        const sc = err.severity === 'critical' ? '#ef4444' : err.severity === 'error' ? '#f97316' : err.severity === 'warn' ? '#f59e0b' : '#94a3b8'
                        const sc2 = err.severity === 'critical' || err.severity === 'error' ? 'error' : err.severity === 'warn' ? 'warning' : 'info'
                        return (
                          <div key={`${err.mac}-${err.timestamp}-${i}`} className={`ov-alert-item ov-group-child ${sc2}`}>
                            <span className="ov-alert-icon" style={{ color: sc }}><AlertOctagon size={12} /></span>
                            <div className="ov-alert-body">
                              <span className="ov-alert-msg">{renderErrMsg(err)}</span>
                            </div>
                            <span className="ov-alert-time">
                              {new Date(err.timestamp).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })
          })()}
        </div>
      </div>

      {/* ── Row 3: Connectivity (full width) ──────────────── */}
      <div className="ov-card ov-connectivity-card">
        <h3 className="ov-card-title"><Wifi size={16} /> Conectividad</h3>
        <div className="ov-conn-two-col">
          <div className="ov-conn-left">
            <RingChart
              value={deviceAnalytics.online}
              total={allDevices.length}
              label="Online"
              color="#10b981"
            />
            <div className="ov-conn-details">
              <div className="ov-conn-row">
                <span className="ov-dot green" /> En línea: {deviceAnalytics.online}
              </div>
              <div className="ov-conn-row">
                <span className="ov-dot gray" /> Offline: {deviceAnalytics.offline}
              </div>
            </div>
          </div>
          <div className="ov-conn-right">
            <div className="ov-model-breakdown-title">Por modelo</div>
            {Object.entries(deviceAnalytics.modelBreakdown)
              .sort(([, a], [, b]) => (b.online + b.offline) - (a.online + a.offline))
              .map(([model, counts]) => (
                <div key={model} className="ov-model-row">
                  <span className="ov-model-name">{model}</span>
                  <div className="ov-model-counts">
                    <span className="ov-model-on">{counts.online} <span className="ov-model-label">on</span></span>
                    <span className="ov-model-sep">/</span>
                    <span className="ov-model-off">{counts.offline} <span className="ov-model-label">off</span></span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* ── Row 3.5: Sensors ────────────────────────────── */}
      <div className="ov-card" style={{ marginBottom: '1.5rem' }}>
        <h3 className="ov-card-title">
          <Thermometer size={16} /> Sensores
          {sensorStats && <span className="ov-card-subtitle">{mergedOnlineDevices.length} dispositivos</span>}
        </h3>
        {sensorStats ? (
          <div className="ov-sensor-grid">
            {sensorStats.temperature && (
              <div className="ov-sensor-card">
                <Thermometer size={18} className="ov-sensor-icon red" />
                <div className="ov-sensor-value">{sensorStats.temperature.avg.toFixed(1)}°C</div>
                <div className="ov-sensor-range">{sensorStats.temperature.min.toFixed(1)}° — {sensorStats.temperature.max.toFixed(1)}°</div>
                <div className="ov-sensor-label">Temperatura</div>
              </div>
            )}
            {sensorStats.humidity && (
              <div className="ov-sensor-card">
                <Droplets size={18} className="ov-sensor-icon blue" />
                <div className="ov-sensor-value">{sensorStats.humidity.avg.toFixed(0)}%</div>
                <div className="ov-sensor-range">{sensorStats.humidity.min.toFixed(0)}% — {sensorStats.humidity.max.toFixed(0)}%</div>
                <div className="ov-sensor-label">Humedad</div>
              </div>
            )}
            {sensorStats.dpv && (
              <div className="ov-sensor-card">
                <Wind size={18} className="ov-sensor-icon green" />
                <div className="ov-sensor-value">{sensorStats.dpv.avg.toFixed(2)}</div>
                <div className="ov-sensor-range">{sensorStats.dpv.min.toFixed(2)} — {sensorStats.dpv.max.toFixed(2)}</div>
                <div className="ov-sensor-label">DPV (kPa)</div>
              </div>
            )}
            {healthStats?.rssi && (
              <div className="ov-sensor-card">
                <Signal size={18} className="ov-sensor-icon amber" />
                <div className="ov-sensor-value">{healthStats.rssi.avg.toFixed(0)} dBm</div>
                <div className="ov-sensor-range">{healthStats.rssi.total} dispositivos</div>
                <div className="ov-sensor-label">Señal WiFi</div>
              </div>
            )}
          </div>
        ) : (
          <div className="ov-empty-sensors">Sin datos de sensores disponibles</div>
        )}
      </div>

      {/* ── Row 3.6: Telemetry Cadence ─────────────────── */}
      {telemetryIntervals && telemetryIntervals.size > 0 && (
        <div className="ov-card" style={{ marginBottom: '1.5rem' }}>
          <h3 className="ov-card-title">
            <Clock size={16} /> Cadencia de Telemetría
            <span className="ov-card-subtitle">{telemetryIntervals.size} dispositivos reportando</span>
          </h3>
          {(() => {
            const intervals = Array.from(telemetryIntervals.values())
            const fleetAvg = intervals.reduce((a, b) => a + b, 0) / intervals.length
            const fleetMin = Math.min(...intervals)
            const fleetMax = Math.max(...intervals)

            // Per-device list with names
            const perDevice = Array.from(telemetryIntervals.entries()).map(([mac, avgMs]) => {
              const dev = allDevices.find(d => (d.MAC || d.mac || '').toUpperCase() === mac)
              const wsDev = (activeDevices || []).find(d => (d.mac || '').toUpperCase() === mac)
              return { mac, name: dev?.name || wsDev?.name || mac, avgMs }
            }).sort((a, b) => a.avgMs - b.avgMs)

            return (
              <div>
                <div className="ov-sensor-grid">
                  <div className="ov-sensor-card">
                    <Clock size={18} className="ov-sensor-icon blue" />
                    <div className="ov-sensor-value">{formatInterval(fleetAvg)}</div>
                    <div className="ov-sensor-range">{formatInterval(fleetMin)} — {formatInterval(fleetMax)}</div>
                    <div className="ov-sensor-label">Promedio flota</div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', marginTop: '0.75rem', maxHeight: '200px', overflowY: 'auto' }}>
                  {perDevice.map(d => {
                    const s = Math.round(d.avgMs / 1000)
                    const color = s <= 30 ? '#10b981' : s <= 120 ? '#f59e0b' : '#ef4444'
                    return (
                      <div key={d.mac} style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                        padding: '0.375rem 0.5rem', borderRadius: '0.5rem',
                        background: 'rgba(255,255,255,0.03)'
                      }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
                        <span style={{ fontSize: '0.8rem', color: '#e2e8f0', flex: 1 }}>{d.name}</span>
                        <span style={{ fontSize: '0.85rem', fontWeight: 700, color }}>{formatInterval(d.avgMs)}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })()}
        </div>
      )}

      {/* ── Row 3.5: DPV Zones + Fleet Tracker ──────────── */}
      <div className="ov-grid-2">
        {/* DPV Zone Map */}
        <div className="ov-card">
          <h3 className="ov-card-title"><Wind size={16} /> Zonas DPV</h3>
          {(() => {
            const devicesWithDpv = mergedOnlineDevices.filter(d => d.dpv != null)
            if (devicesWithDpv.length === 0) return <div className="ov-empty-sensors">Sin datos DPV disponibles</div>

            const getDpvZone = (dpv) => {
              if (dpv < 0.4) return { zone: 'Peligro bajo', color: '#3b82f6', bg: 'rgba(59,130,246,0.15)' }
              if (dpv < 0.8) return { zone: 'Propagación', color: '#06b6d4', bg: 'rgba(6,182,212,0.12)' }
              if (dpv < 1.2) return { zone: 'Vegetativo', color: '#10b981', bg: 'rgba(16,185,129,0.12)' }
              if (dpv <= 1.5) return { zone: 'Floración', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' }
              return { zone: 'Peligro alto', color: '#ef4444', bg: 'rgba(239,68,68,0.15)' }
            }

            const zones = { 'Peligro bajo': 0, 'Propagación': 0, 'Vegetativo': 0, 'Floración': 0, 'Peligro alto': 0 }
            devicesWithDpv.forEach(d => { const z = getDpvZone(d.dpv); zones[z.zone]++ })

            return (
              <div>
                {/* Zone summary bar */}
                <div style={{ display: 'flex', gap: '2px', height: '28px', borderRadius: '6px', overflow: 'hidden', marginBottom: '0.75rem' }}>
                  {[
                    { key: 'Peligro bajo', color: '#3b82f6' },
                    { key: 'Propagación', color: '#06b6d4' },
                    { key: 'Vegetativo', color: '#10b981' },
                    { key: 'Floración', color: '#f59e0b' },
                    { key: 'Peligro alto', color: '#ef4444' },
                  ].map(z => {
                    const pct = (zones[z.key] / devicesWithDpv.length) * 100
                    if (pct === 0) return null
                    return (
                      <div key={z.key} style={{
                        width: `${pct}%`, background: z.color, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: '0.65rem', color: '#fff', fontWeight: 700,
                        minWidth: zones[z.key] > 0 ? '18px' : 0
                      }}>{zones[z.key]}</div>
                    )
                  })}
                </div>
                {/* Legend */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1rem', marginBottom: '0.75rem' }}>
                  {[
                    { label: 'Peligro bajo (<0.4)', color: '#3b82f6' },
                    { label: 'Propagación (0.4-0.8)', color: '#06b6d4' },
                    { label: 'Vegetativo (0.8-1.2)', color: '#10b981' },
                    { label: 'Floración (1.0-1.5)', color: '#f59e0b' },
                    { label: 'Peligro alto (>1.5)', color: '#ef4444' },
                  ].map(z => (
                    <span key={z.label} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.7rem', color: '#94a3b8' }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: z.color, flexShrink: 0 }} />
                      {z.label}
                    </span>
                  ))}
                </div>
                {/* Device DPV list */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', maxHeight: '200px', overflowY: 'auto' }}>
                  {devicesWithDpv.sort((a, b) => b.dpv - a.dpv).map(d => {
                    const z = getDpvZone(d.dpv)
                    return (
                      <div key={d.mac} style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                        padding: '0.375rem 0.5rem', borderRadius: '0.5rem', background: z.bg
                      }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: z.color, flexShrink: 0 }} />
                        <span style={{ fontSize: '0.8rem', color: '#e2e8f0', flex: 1 }}>{d.name || d.mac}</span>
                        <span style={{ fontSize: '0.85rem', fontWeight: 700, color: z.color }}>{Number(d.dpv).toFixed(2)}</span>
                        <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{z.zone}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })()}
        </div>

        {/* Fleet Firmware Tracker */}
        <div className="ov-card">
          <h3 className="ov-card-title"><HardDrive size={16} /> Fleet & Firmware</h3>
          {(() => {
            // Extract firmware per device from allDevices status + merged live data
            const deviceFirmwares = []
            allDevices.forEach(d => {
              const mac = (d.MAC || d.mac || '').toUpperCase()
              // firmwareVersion is already resolved by the backend
              let fw = d.firmwareVersion || d.liveData?.firmwareVersion || null
              // Try merged online data
              if (!fw) {
                const merged = mergedOnlineDevices.find(m => (m.mac || '').toUpperCase() === mac)
                fw = merged?.firmwareVersion || null
              }
              deviceFirmwares.push({
                mac: d.MAC || d.mac, name: d.name, model: d.model || 'N/A',
                firmware: fw || 'Sin datos',
                isOnline: !offlineMACsSet.has(mac) && (d.isOnline || activeMACsSet.has(mac))
              })
            })

            // Aggregate firmware versions
            const firmwareMap = {}
            const modelMap = {}
            deviceFirmwares.forEach(d => {
              firmwareMap[d.firmware] = (firmwareMap[d.firmware] || 0) + 1
              modelMap[d.model] = (modelMap[d.model] || 0) + 1
            })
            const fwEntries = Object.entries(firmwareMap).sort((a, b) => b[1] - a[1])
            const modelEntries = Object.entries(modelMap).sort((a, b) => b[1] - a[1])
            const totalFw = deviceFirmwares.length
            const fwColors = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4']
            const fwColorMap = Object.fromEntries(fwEntries.map(([fw], i) => [fw, fwColors[i % fwColors.length]]))
            // Determine latest firmware (most common non-"Sin datos" version)
            const latestFw = fwEntries.find(([fw]) => fw !== 'Sin datos')?.[0] || null

            return (
              <div>
                {/* Model distribution */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Modelos</div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {modelEntries.map(([model, count]) => (
                      <div key={model} style={{
                        padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
                        background: model.includes('BOX') ? 'rgba(99, 102, 241, 0.15)' : model.includes('MINI') ? 'rgba(16, 185, 129, 0.15)' : 'rgba(100,100,100,0.15)',
                        border: `1px solid ${model.includes('BOX') ? 'rgba(99, 102, 241, 0.3)' : model.includes('MINI') ? 'rgba(16, 185, 129, 0.3)' : 'rgba(100,100,100,0.3)'}`,
                        display: 'flex', alignItems: 'center', gap: '0.5rem'
                      }}>
                        <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff' }}>{count}</span>
                        <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{model}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Firmware version distribution bars */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Versiones de Firmware</div>
                  {fwEntries.length === 0 ? (
                    <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>Sin datos de firmware</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                      {fwEntries.map(([fw, count]) => {
                        const pct = totalFw > 0 ? (count / totalFw) * 100 : 0
                        const color = fwColorMap[fw]
                        return (
                          <div key={fw} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontSize: '0.8rem', color: '#e2e8f0', minWidth: '100px' }}>{fw}</span>
                            <div style={{ flex: 1, height: '8px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', overflow: 'hidden' }}>
                              <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: '4px', transition: 'width 0.5s ease' }} />
                            </div>
                            <span style={{ fontSize: '0.75rem', color: '#94a3b8', minWidth: '60px', textAlign: 'right' }}>{count} ({pct.toFixed(0)}%)</span>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
                {/* Per-device firmware list */}

              </div>
            )
          })()}
        </div>
      </div>

      {/* ── Row 4: WiFi Quality + Device Health ─────────── */}
      {healthStats && (
        <div className="ov-grid-2">
          {/* WiFi Signal Distribution */}
          <div className="ov-card">
            <h3 className="ov-card-title">
              <Signal size={16} /> Calidad de señal WiFi
            </h3>
            <div className="ov-bar-chart">
              {[
                { label: 'Excelente (>-50)', value: healthStats.rssi.dist.excellent, color: 'green' },
                { label: 'Buena (-60 a -50)', value: healthStats.rssi.dist.good, color: 'green' },
                { label: 'Aceptable (-70 a -60)', value: healthStats.rssi.dist.fair, color: 'amber' },
                { label: 'Débil (<-70)', value: healthStats.rssi.dist.weak, color: 'red' },
              ].map(item => {
                const pct = healthStats.rssi.total > 0 ? (item.value / healthStats.rssi.total) * 100 : 0
                return (
                  <div className="ov-bar-row" key={item.label}>
                    <span className="ov-bar-label">{item.label}</span>
                    <div className="ov-bar-track">
                      <div className={`ov-bar-fill ${item.color}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="ov-bar-value">{item.value}</span>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Uptime / Health Summary */}
          <div className="ov-card">
            <h3 className="ov-card-title"><Clock size={16} /> Uptime de dispositivos</h3>
            <div className="ov-uptime-grid">
              <div className="ov-uptime-item">
                <div className="ov-uptime-label">Promedio</div>
                <div className="ov-uptime-value">{formatUptime(healthStats.uptime.avg)}</div>
              </div>
              <div className="ov-uptime-item">
                <div className="ov-uptime-label">Máximo</div>
                <div className="ov-uptime-value">{formatUptime(healthStats.uptime.max)}</div>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* ── Row 5: Active Devices Table ──────────────────── */}
      {mergedOnlineDevices.length > 0 && (
        <div className="ov-card">
          <h3 className="ov-card-title">
            <Activity size={16} /> Dispositivos activos ({mergedOnlineDevices.length})
          </h3>
          <div className="ov-table-wrap">
            <table className="ov-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Nombre</th>
                  <th>MAC</th>
                  <th>Modelo</th>
                  <th>Sensor</th>
                  <th>Temp</th>
                  <th>Hum</th>
                  <th>DPV</th>
                  <th>WiFi</th>
                  <th>Uptime</th>
                  <th>Cadencia</th>
                  <th>Firmware</th>
                </tr>
              </thead>
              <tbody>
                {mergedOnlineDevices.map(d => {
                  const rq = d.rssi != null ? rssiQuality(d.rssi) : null
                  return (
                    <tr key={d.mac} className="ov-table-row--clickable" onClick={() => navigate(`/devices/${encodeURIComponent(d.mac)}`)}>
                      <td><span className="ov-dot-inline green" /></td>
                      <td>{d.name || '-'}</td>
                      <td className="mono">{d.mac}</td>
                      <td><span className={`ov-model ${(d.model || '').toLowerCase().includes('box') ? 'box' : (d.model || '').toLowerCase().includes('mini') ? 'mini' : ''}`}>{d.model || '-'}</span></td>
                      <td>{d.sensorName || '-'}</td>
                      <td>{d.temperature != null ? `${Number(d.temperature).toFixed(1)}°` : '-'}</td>
                      <td>{d.humidity != null ? `${Number(d.humidity).toFixed(0)}%` : '-'}</td>
                      <td>{d.dpv != null ? Number(d.dpv).toFixed(2) : '-'}</td>
                      <td>{rq ? <><MiniGauge value={rq.pct} color={rq.color} /> <span className="ov-rssi-val">{d.rssi}</span></> : '-'}</td>
                      <td>{d.uptime ? formatUptime(d.uptime) : '-'}</td>
                      <td>{(() => {
                        const interval = telemetryIntervals?.get((d.mac || '').toUpperCase())
                        return interval ? formatInterval(interval) : '-'
                      })()}</td>
                      <td className="ov-fw-version">{d.firmwareVersion || '-'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
